<template>
  <table>
    <caption>
      {{
        caption
      }}
    </caption>
    <thead>
      <tr>
        <th
          v-if="topLeftHeader || (leftHeaders && leftHeaders.length > 0)"
          :colspan="topLeftHeader?.colspan"
          :style="topLeftHeader?.style"
          class="left"
          scope="col"
          :class="topLeftHeader?.class"
        >
          <slot
            v-if="topLeftHeader"
            name="header-cell"
            :cell="topLeftHeader"
            position="topLeft"
          ></slot>
        </th>
        <th
          v-for="(header, i) in topHeaders"
          :key="i"
          scope="col"
          :colspan="header.colspan"
          :style="header.style"
          :class="header.class"
        >
          <slot v-if="header" name="header-cell" :cell="header" position="top"></slot>
        </th>
        <th
          v-if="topRightHeader || (rightHeaders && rightHeaders.length > 0)"
          scope="col"
          :colspan="topRightHeader?.colspan"
          :style="topRightHeader?.style"
          class="right"
          :class="topRightHeader?.class"
        >
          <slot
            v-if="topRightHeader"
            name="header-cell"
            :cell="topRightHeader"
            position="topRight"
          ></slot>
        </th>
        <th v-if="bodyHasScrollbars" id="compensator" class="scrollbar-compensator"></th>
      </tr>
    </thead>
    <tbody ref="bodyRef">
      <tr
        v-for="(row, rowIndex) in sortedBody"
        :key="rowIndex"
        :tabindex="tabbableRows ? 0 : -1"
        @click="(e) => onRowClick(e, row)"
        @keydown.enter="(e) => onRowClick(e, row)"
        @keydown.space="(e) => onRowClick(e, row, true)"
      >
        <th
          v-if="leftHeaders && leftHeaders.length > 0 && leftHeaders[rowIndex]"
          scope="row"
          :rowspan="leftHeaders[rowIndex]?.rowspan"
          :style="leftHeaders[rowIndex]?.style"
          class="left"
          :class="leftHeaders[rowIndex]?.class"
        >
          <slot name="header-cell" :cell="leftHeaders[rowIndex]" position="left"></slot>
        </th>
        <template v-for="(cell, cellIndex) in row" :key="`${rowIndex}-${cellIndex}`">
          <td
            v-if="cell"
            :style="cell.style"
            :class="cell.class"
            @mouseenter="(e) => onMouseEnter(e, cell)"
            @mouseleave="onMouseLeave"
          >
            <slot
              name="body-cell"
              :cell="cell"
              :row-index="rowIndex"
              :cell-index="cellIndex"
              position="body"
            ></slot>
          </td>
        </template>
        <th
          v-if="rightHeaders && rightHeaders.length > 0 && rightHeaders[rowIndex]"
          scope="row"
          :rowspan="rightHeaders[rowIndex]?.rowspan"
          :style="rightHeaders[rowIndex]?.style"
          class="right"
          :class="rightHeaders[rowIndex]?.class"
        >
          <slot name="header-cell" :cell="rightHeaders[rowIndex]" position="right"></slot>
        </th>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <th
          v-if="bottomLeftHeader || (leftHeaders && leftHeaders.length > 0)"
          scope="row"
          :colspan="bottomLeftHeader?.colspan"
          :style="bottomLeftHeader?.style"
          :class="bottomLeftHeader?.class"
          class="left"
        >
          <slot
            v-if="bottomLeftHeader"
            name="header-cell"
            :cell="bottomLeftHeader"
            position="bottomLeft"
          ></slot>
        </th>
        <template v-for="(header, i) in bottomHeaders" :key="i">
          <th
            v-if="header"
            scope="col"
            :colspan="header.colspan"
            :style="header.style"
            :class="header.class"
          >
            <slot name="header-cell" :cell="header" position="bottom"></slot>
          </th>
        </template>
        <th
          v-if="bottomRightHeader || (rightHeaders && rightHeaders.length > 0)"
          scope="col"
          :colspan="bottomRightHeader?.colspan"
          :style="bottomRightHeader?.style"
          class="right"
          :class="bottomRightHeader?.class"
        >
          <slot
            v-if="bottomRightHeader"
            name="header-cell"
            :cell="bottomRightHeader"
            position="bottomRight"
          ></slot>
        </th>
        <th v-if="bodyHasScrollbars" id="compensator" class="scrollbar-compensator"></th>
      </tr>
      <tr>
        <th
          v-if="bottomLeftHeader || bottomRightHeader || (leftHeaders && leftHeaders.length > 0)"
          scope="row"
        ></th>
        <th :colspan="footerColSpan" scope="row">
          <slot name="footer" position="footer"></slot>
        </th>
      </tr>
    </tfoot>
  </table>
</template>
<script lang="ts" setup generic="HeaderProps, BodyProps">
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { Table } from './table-base'

type Props = {
  bodyHeight?: string
  leftWidth?: string
  rightWidth?: string
  leftTextAlign?: 'left' | 'center' | 'right'
  rightTextAlign?: 'left' | 'center' | 'right'
  layout?: 'fixed' | 'auto'
  caption: string
  sortBy?: (i: number) => string | number
  sortDirection?: 'asc' | 'desc'
  tabbableRows?: boolean
  table?: Table<HeaderProps, BodyProps>
}

type Emits = {
  (event: 'mouseEnterCell', cell: BodyProps, centerX: number, centerY: number): void
  (event: 'mouseLeaveCell'): void
  (event: 'onClickRow', row?: BodyProps[]): void
  (event: 'nearEnd', edge: 'top' | 'bottom'): void
}

const props = withDefaults(defineProps<Props>(), {
  bodyHeight: 'auto',
  leftWidth: 'auto',
  rightWidth: 'auto',
  layout: 'auto',
  leftTextAlign: 'left',
  rightTextAlign: 'right',
  tabbableRows: false,
  table: undefined,
  sortBy: undefined,
  sortDirection: 'asc'
})

const emit = defineEmits<Emits>()

const { table } = toRefs(props)

const bodyRef = ref<HTMLElement | null>(null)

const hovered = ref<BodyProps | undefined>()

const nearEdge = ref<boolean>(false)

const order = computed(() => {
  const indices: number[] = []
  for (let i = 0; i < (table.value?.body?.length ?? 0); i++) {
    indices.push(i)
  }
  if (!props.sortBy) {
    return indices
  }
  const sortBy = props.sortBy
  const sorted = indices.toSorted((a, b) => {
    if (props.sortDirection === 'desc') {
      return sortBy(a) < sortBy(b) ? 1 : -1
    } else {
      return sortBy(a) > sortBy(b) ? 1 : -1
    }
  })
  return sorted
})

const topHeaders = computed(() => table.value?.headers?.top)
const bottomHeaders = computed(() => table.value?.headers?.bottom)
const leftHeaders = computed(() =>
  table.value?.headers?.left ? order.value.map((i) => table.value?.headers?.left?.[i]) : undefined
)
const rightHeaders = computed(() =>
  table.value?.headers?.right ? order.value.map((i) => table.value?.headers?.right?.[i]) : undefined
)
const topLeftHeader = computed(() => table.value?.headers?.topLeft)
const topRightHeader = computed(() => table.value?.headers?.topRight)
const bottomLeftHeader = computed(() => table.value?.headers?.bottomLeft)
const bottomRightHeader = computed(() => table.value?.headers?.bottomRight)
const leftTextAlignWebkit = computed(() => `-webkit-${props.leftTextAlign ?? 'center'}`)
const rightTextAlignWebkit = computed(() => {
  return `-webkit-${props.rightTextAlign ?? 'center'}`
})

const sortedBody = computed(() => {
  return order.value.map((i) => table.value?.body[i])
})

const footerColSpan = computed(() => {
  return (
    (table.value?.body?.[0]?.length ??
      topHeaders.value?.length ??
      bottomHeaders.value?.length ??
      -1) + 1
  )
})

const bodyHasScrollbars = computed(() => {
  if (!bodyRef.value) {
    return false
  }
  return bodyRef.value.scrollHeight > bodyRef.value.clientHeight
})

const lastScrollTop = ref<number>(0)

function isWithinPercentageOfRange(value: number, totalRange: number, percentage: number): boolean {
  const percentageValue = totalRange * (percentage / 100)
  return value <= percentageValue || value >= totalRange - percentageValue
}

function scroll() {
  if (!bodyRef.value) return
  const { scrollTop, offsetHeight, scrollHeight } = bodyRef.value
  if (isWithinPercentageOfRange(scrollTop, scrollHeight - offsetHeight, 30)) {
    emit('nearEnd', lastScrollTop.value > scrollTop ? 'top' : 'bottom')
    nearEdge.value = true
  } else {
    nearEdge.value = false
  }
  lastScrollTop.value = scrollTop
}

onMounted(() => {
  if (!bodyRef.value) {
    return
  }
  bodyRef.value.addEventListener('scroll', scroll)
  const unsub = watch(
    () => table.value?.body,
    () => {
      nearEdge.value = false
    }
  )

  return () => {
    if (!bodyRef.value) {
      return
    }
    bodyRef.value.removeEventListener('scroll', scroll)
    unsub()
  }
})

function onMouseEnter(event: Event, cell: BodyProps) {
  hovered.value = cell
  const target = event.target as HTMLElement
  const rect = target.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  emit('mouseEnterCell', cell, centerX, centerY)
}

function onMouseLeave() {
  hovered.value = undefined
  emit('mouseLeaveCell')
}

function onRowClick(event: Event, row?: BodyProps[], preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  emit('onClickRow', row)
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';

table {
  display: block;
  width: 100%;
  border-collapse: collapse;
  padding: 0;
  border-spacing: 0;
  border: 0;

  .left {
    width: v-bind(leftWidth);
  }

  .right {
    width: v-bind(rightWidth);
  }

  thead,
  tfoot {
    display: block;
    width: 100%;
  }

  tbody {
    display: block;
    width: 100%;
    max-height: v-bind(bodyHeight);
    overflow-x: hidden;
    overflow-y: auto;
    @include mixins.scrollbar-vertical;
  }

  thead,
  tfoot,
  tbody tr {
    display: table;
    width: 100%;
    table-layout: v-bind(layout);
  }

  caption {
    display: none;
  }

  th,
  td {
    text-align: center;
    padding: 0;
    word-wrap: break-word;
  }

  .scrollbar-compensator {
    width: 6px;
  }

  tr th:last-child,
  tr td:last-child {
    text-align: v-bind(rightTextAlign);
    text-align: v-bind(rightTextAlignWebkit);
  }

  tr th:first-child,
  tr td:first-child {
    text-align: v-bind(leftTextAlign);
    text-align: v-bind(leftTextAlignWebkit);
  }
}
</style>
